// The /radio page's profile source: Spotify Soloist's now-playing state
// (polled from /api/soloist/now, see src/services/soloistService.js) in
// place of the Sonos zones. It translates Soloist's payload into the Sonos
// state shape sonos-visual-profile.js's buildProfile reads, and hands it
// to the shared createVisualProfileSource - so the profile the visualizers,
// the orchestrator and the lyrics/analysis pipeline consume is exactly the
// one they get on party-mode.html, field for field.
//
// The one thing that IS different here is time. Listeners hear the Icecast
// stream, not Soloist's output: the encoder, Icecast's burst-on-connect
// buffer, the tunnel and the browser's own buffering put what is heard a
// few seconds BEHIND where Soloist says it is. So the WHOLE state shown is
// the one Soloist reported latencySeconds ago: the last few polled states
// are kept, and the newest one old enough is the one handed to
// buildProfile, its position advanced by the time elapsed since. That
// covers both the beat grid/drops/lyrics (position) AND the track change
// itself (title, cover, palette): Spotify's automatic transitions start the
// next track before Soloist announces it, and announcing it on the page the
// instant Soloist does put the new cover up while the listener was still
// hearing the previous track. The lag is not measurable from the page
// (browsers expose no ICY timing), so it is a constant:
// RADIO_LATENCY_DEFAULT_S, overridable with ?latency=<seconds> for tuning
// by eye. Before the page has been open for that long, the oldest state it
// has is used (position clamped at 0), so the display is continuous from
// the first poll on - no blank wait, no jump when the delay kicks in.
import { API_BASE } from './api-base.js';
import { createVisualProfileSource } from './sonos-visual-profile.js';

export const RADIO_LATENCY_DEFAULT_S = 8;
// LA LATENCE EST MESURÉE, plus devinée (28/09/2026). Retrouvé par corrélation
// dans l'audio de référence du morceau (webradiopi, en temps du morceau) : ce
// qu'un navigateur entend est en retard sur Soloist d'une part FIXE — le Pi,
// la rafale d'Icecast à la connexion, le tunnel — plus le temps que CE
// lecteur a passé sans jouer depuis sa connexion (démarrage, coupures).
// Sur quatre connexions, avec et sans pause, la latence réelle (6,25 à 6,35 s)
// moins ce temps non joué valait 2,6 à 2,8 s. La constante de 8 s mettait donc
// la page 1,7 s derrière le son dans Chrome, et 3 à 4 s sur un lecteur qui
// démarre plus vite : les drops tombaient après le son.
export const RADIO_LATENCE_FIXE_S = 2.7;
// Polled states kept for the delayed replay: the latency plus a margin, at
// one poll every 3 s - a handful of small objects.
const HISTORY_EXTRA_S = 15;

export function radioLatencySeconds() {
  if (typeof location === 'undefined') return RADIO_LATENCY_DEFAULT_S;
  const raw = new URLSearchParams(location.search).get('latency');
  if (raw == null) return RADIO_LATENCY_DEFAULT_S;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : RADIO_LATENCY_DEFAULT_S;
}

// The "room" the transport callbacks key on (main.js checks profile.roomName
// to know whether anything is playing) - there is one radio, so one name.
export const RADIO_ROOM_NAME = 'PiRadio';

// Same rule as pickZoneState: only a PLAYING source yields a state; paused
// or idle reads as "nothing playing", which is what the overlay shows.
// positionS is the already-delayed position (see pickDelayed).
function toSonosShape(now, positionS) {
  if (!now || now.status !== 'playing' || !now.title) return null;
  const durationS = now.durationMs > 0 ? now.durationMs / 1000 : 0;
  return {
    roomName: RADIO_ROOM_NAME,
    state: {
      playbackState: 'PLAYING',
      elapsedTime: durationS > 0 ? Math.min(positionS, durationS) : positionS,
      currentTrack: {
        title: now.title,
        artist: now.artist || '',
        album: now.album || '',
        duration: durationS,
        // Spotify's CDN, https - /api/art-proxy accepts it for the palette.
        absoluteAlbumArtUri: now.artworkUrl || null,
        // Prefixed so detectSource classes it 'radio' (lyrics offset 0).
        uri: 'x-piradio:' + (now.uri || '')
      }
    }
  };
}

// The state to show NOW: the newest polled state that is at least
// latencySeconds old, or the oldest one there is while the page is younger
// than that. Its position is Soloist's at receipt, plus the time elapsed
// since, minus the latency - i.e. exactly where the listener's audio is.
function pickDelayed(history, nowMs, latencySeconds) {
  const cutoff = nowMs - latencySeconds * 1000;
  let chosen = null;
  for (let i = history.length - 1; i >= 0; i--) {
    if (history[i].receivedAt <= cutoff) { chosen = history[i]; break; }
  }
  // RIEN AVANT D'ÊTRE SÛR (28/09/2026) : tant que la page n'a pas d'état
  // aussi vieux que la latence, elle ne sait pas ce que l'auditeur entend —
  // montrer le titre que Soloist joue maintenant afficherait la pochette et
  // le titre du morceau suivant sur la fin du précédent. Rien jusque-là.
  if (!chosen) return null;
  const now = chosen.now;
  let positionS = now.positionMs / 1000;
  if (now.status === 'playing') positionS += (nowMs - chosen.receivedAt) / 1000;
  positionS -= latencySeconds;
  return toSonosShape(now, Math.max(0, positionS));
}

// options.override (optional): called first on every poll. Returning
// undefined means "poll Soloist as usual"; anything else (a {state, roomName}
// object or null for idle) is used instead - the /radio page's external
// stations feed the same visualizer pipeline through this (see
// external-radio.js profileState).
export function createSoloistVisualProfileSource(options, onUpdate, onError) {
  // Un nombre (réglage fixe), ou une fonction qui rend la latence du moment
  // (mesurée par la page) ou null quand elle ne la connaît pas encore.
  const latenceFn = options && typeof options.latencySeconds === 'function' ? options.latencySeconds : null;
  const latencyFixe = options && typeof options.latencySeconds === 'number'
    ? options.latencySeconds : radioLatencySeconds();
  const latenceDuMoment = function () {
    const v = latenceFn ? latenceFn() : null;
    return Number.isFinite(v) ? Math.max(0.5, Math.min(30, v)) : latencyFixe;
  };
  const override = options && typeof options.override === 'function' ? options.override : null;
  const history = [];
  return createVisualProfileSource(function fetchPicked() {
    if (override) {
      const forced = override();
      if (forced !== undefined) return Promise.resolve(forced);
    }
    return fetch(API_BASE + '/api/soloist/now')
      .then(function (res) { return res.json(); })
      .then(function (now) {
        const nowMs = Date.now();
        const latency = latenceDuMoment();
        history.push({ now: now, receivedAt: nowMs });
        // Keep everything younger than latency + margin, plus the newest
        // entry older than that (it may still be the one to show).
        const keepFrom = nowMs - (Math.max(latency, RADIO_LATENCY_DEFAULT_S) + HISTORY_EXTRA_S) * 1000;
        while (history.length > 1 && history[1].receivedAt <= keepFrom) history.shift();
        return pickDelayed(history, nowMs, latency);
      });
  }, onUpdate, onError);
}

// Left/Right on the radio page: skips Soloist itself, i.e. the whole web
// radio - mirrors transport-client.js's sendTransportCommand.
export function sendSoloistTransport(action) {
  return fetch(API_BASE + '/api/soloist/transport', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: action })
  }).then(function (res) {
    if (!res.ok) throw new Error('soloist transport request failed: ' + res.status);
    return res.json();
  });
}
